/**
 * Toast 提示组件（xiaDanUser 版，配色用红色主色）。
 *
 * 用法：
 *   // layout.tsx 挂一次：
 *   import { Toaster } from "@/components/ui/toast";
 *   <body> ... <Toaster /> </body>
 *
 *   // 任何地方触发：
 *   import { toast } from "@/lib/toast-store";
 *   toast.success("成功");
 */
"use client";

import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToastStore, type ToastVariant } from "@/lib/toast-store";

/**
 * 不同 variant 的配色。
 * 注意：xiaDanUser 主色是红色系（#F53F3F），success=绿色，warning=橙色，info=用主色红色。
 */
const VARIANT_STYLES: Record<ToastVariant, { bg: string; icon: React.ReactNode; ring: string }> = {
  success: {
    bg: "bg-[#00B42A]/95 text-white",
    ring: "ring-[#00B42A]/30",
    icon: <CheckCircle2 size={18} className="shrink-0" />,
  },
  error: {
    bg: "bg-[#F53F3F]/95 text-white",
    ring: "ring-[#F53F3F]/30",
    icon: <XCircle size={18} className="shrink-0" />,
  },
  info: {
    bg: "bg-[#F53F3F]/95 text-white",
    ring: "ring-[#F53F3F]/30",
    icon: <Info size={18} className="shrink-0" />,
  },
  warning: {
    bg: "bg-[#FF7D00]/95 text-white",
    ring: "ring-[#FF7D00]/30",
    icon: <AlertTriangle size={18} className="shrink-0" />,
  },
};

function ToastCard({
  id,
  variant,
  message,
  onClose,
}: {
  id: string;
  variant: ToastVariant;
  message: string;
  onClose: (id: string) => void;
}) {
  const style = VARIANT_STYLES[variant];
  return (
    <div
      className={cn(
        "flex items-start gap-2.5 px-4 py-3 rounded-xl shadow-lg",
        "ring-1 backdrop-blur-sm max-w-[90vw] md:max-w-md",
        "animate-toast-in",
        style.bg,
        style.ring,
      )}
      onClick={() => onClose(id)}
      role="status"
      aria-live="polite"
    >
      <div className="pt-0.5">{style.icon}</div>
      <div className="flex-1 text-sm leading-relaxed break-words whitespace-pre-wrap">
        {message}
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose(id);
        }}
        className="shrink-0 w-5 h-5 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors -mr-1"
        aria-label="关闭提示"
      >
        <X size={14} />
      </button>
    </div>
  );
}

export function Toaster() {
  const { toasts, remove } = useToastStore();

  if (toasts.length === 0) return null;

  return (
    <div
      className={cn(
        "fixed left-1/2 -translate-x-1/2 top-4 z-[300]",
        "flex flex-col gap-2 items-center pointer-events-none",
      )}
    >
      <div className="pointer-events-auto flex flex-col gap-2 items-center w-full">
        {toasts.map((t) => (
          <ToastCard
            key={t.id}
            id={t.id}
            variant={t.variant}
            message={t.message}
            onClose={remove}
          />
        ))}
      </div>
    </div>
  );
}

export { ToastCard };
